<template lang="pug">
  el-row#question-builder
    
    // Main Form
    el-form#form-builder(
      :model="question"
      :rules="rules"
      ref="question"
      label-position="top")

      // Question Title
      el-row(:gutter="80")
        el-col(:span="12")    
          el-form-item(prop="title")    
            label Question Title
            el-input(
              placeholder="Enter Quiz Title"
              minlength="1"
              :maxlength="uiSettings.TITLE"
              v-model="question.title") 
        // Score
        el-col(:span="12" style="float: right; text-align: right")            
          label Total Score         
          span(class="totalScore") {{ totalScore }}
  
      // Question Text
      el-form-item(prop="text")
        label Question Text
        rich-editor(
          minlength="1"
          :maxlength="uiSettings.CONTENT"
          v-model="question.text")  

      el-form-item
        label Test Cases

        div(
          v-for="testCase, testIndex in testCases"
          :key="testIndex"
          class="testCasePanel"
        )
          i(class="el-icon-notebook-2 testLabel" style="font-size: 1.35em")
          span(class="testLabel") Test Case {{ testIndex + 1 }}
          el-button.margin-left-15(
            type="danger"
            size="mini"
            class="testButton"
            icon="el-icon-document-delete"
            @click="removeTestCase(testIndex)"
          ) Remove

          div(class="gradeField")
            el-switch(v-model="testCase.initVar.isActive" :active-color="switchColor")
            span(class="fieldLabel") Initial variables
            initial-variable-form(
              v-show="testCase.initVar.isActive"
              :item-list="testCase.initVar.vars"
              ref="coding-initVar"
            )
          div(class="gradeField")
            el-switch(v-model="testCase.varName.isActive" :active-color="switchColor")
            span(class="fieldLabel") Variable names
            criteria-list-form(
              v-show="testCase.varName.isActive"
              :item-list="testCase.varName.vars"
              valuePlaceholder="Variable Name"
              ref="coding-varName"
            )

          div(class="gradeField")
            el-switch(v-model="testCase.varType.isActive" :active-color="switchColor")
            span(class="fieldLabel") Variable types
            variable-type-form(
              v-show="testCase.varType.isActive"
              :item-list="testCase.varType.vars"
              ref="coding-varType"
            )

          div(class="gradeField")
            el-switch(v-model="testCase.varValue.isActive" :active-color="switchColor")
            el-tooltip(content="Check variable with condition, e.g. x == 1 or abs(x-1) < 0.001" placement="top")
              span(class="fieldLabel") Variable equations

            criteria-list-form(
              v-show="testCase.varValue.isActive"
              :item-list="testCase.varValue.vars"
              valuePlaceholder="Variable Equation"
              valueAttr="equation"
              ref="coding-varEquation"
            )

          div(class="gradeField" style="border-bottom: none;")
            el-switch(v-model="testCase.graph.isActive" :active-color="switchColor")
            span(class="fieldLabel") Graphs
            graph-criteria-form(
              v-show="testCase.graph.isActive"
              :graph="testCase.graph"
              ref="coding-graph"
            )
      el-row  
        el-button(
          type="primary"
          class="testButton"
          size="small"
          icon="el-icon-document-add"
          @click="addNewTestCase"
        ) Add test case

      el-row.margin-top-15
        el-col(:span="12")    
          // Tags
          el-form-item(
            v-if="$store.state.adminData.userId !== null"
            prop="tags")
            label Tags
            br
            el-select(
              v-model="searchParams"
              multiple
              filterable
              reserve-keyword
              placeholder=""
              no-data-text="No tags found"
              no-match-text="No tag matches found"
              style="width: 100%;")
              el-option#tag-options(
                v-for="item in tagsList"
                :key="item.id"
                :label="item.text"
                :value="item.id"
              )

      // Buttons
      el-row(:gutter="20")
        el-col#buttons(:span="24" style="margin-bottom: 2%;")
          el-button-group(style="float: right;")
            el-button(
              class="normal-button"
              icon="el-icon-error"
              @click="onCancelClicked") Cancel
            el-button(
              class="primary-button"
              icon="el-icon-document"
              @click="onSubmitValidate") Save                     
            
</template>

<script>
  import RichEditor from '@/components/utils/RichEditor'
  import CriteriaListForm from './CodingCriteria/CriteriaListForm.vue'
  import VariableTypeForm from './CodingCriteria/VariableTypeForm.vue'
  import GraphCriteriaForm from './CodingCriteria/GraphCriteriaForm.vue'
  import InitialVariableForm from './CodingCriteria/InitialVariableForm.vue'
  import utilMixin from '@/components/mixins/utilMixin'
  import cloneDeep from 'lodash/cloneDeep'

  import { MAX_LENGTH } from '@/defines/constants'
  import { TEST_CASE_DEFAULT } from '@/defines/questionTypeDefault'

  export default {
    name: 'CodingForm',

    components: {
      RichEditor,
      CriteriaListForm,
      VariableTypeForm,
      GraphCriteriaForm,
      InitialVariableForm,
    },

    mixins: [utilMixin],

    props: {
      question: {
        type: Object,
        required: true,
        default: () => {},
      },            

      action: {
        type: String,
        required: true,
      },

      tagsList: {
        type: Array,
        default: () => { return [] },
      },
    },

    data() {
      return {
        testCases: [],

        excludeData: ['createdAt', 'updatedAt'],
        searchParams: [],

        uiSettings: MAX_LENGTH,
        rules: {
          title: [
            { required: true, message: 'Please input the Question\'s title', trigger: 'blur' },
            { min: 1, max: MAX_LENGTH.TITLE,
              message: 'Length should be 0 to ' + MAX_LENGTH.TITLE, trigger: 'blur' },
          ],
          text: [
            { required: true, message: 'Please input the Question text', trigger: 'blur' },
            { min: 1, max: MAX_LENGTH.CONTENT,
              message: 'Length should be 0 to ' + MAX_LENGTH.CONTENT, trigger: 'blur' },
          ],
        },
        
        switchColor: "#40c4ff",
      }
    },

    computed: {
      totalScore() {
        let score = 0
        const testScore = this.testCases.reduce((sum, test) => {
          score += test.varName.isActive ? this.sumScore(test.varName.vars) : 0
          score += test.varType.isActive ? this.sumScore(test.varType.vars) : 0
          score += test.varValue.isActive ? this.sumScore(test.varValue.vars) : 0

          if (test.graph.isActive) {
            score += test.graph.labels.title.score
            score += test.graph.labels.xLabel.score + test.graph.labels.yLabel.score
            score += test.graph.legends.isActive ? this.sumScore(test.graph.legends.legs) : 0
            score += test.graph.limit.xLim.score + test.graph.limit.yLim.score
            score += test.graph.scale.xScale.score + test.graph.scale.yScale.score
            score += test.graph.line.lines.reduce((s, x) => s +
              x.xData.score + x.yData.score + x.color.score + x.style.score + x.marker.score, 0)
          }
          return score
        }, 0)
        return testScore
      },
    },

    mounted() {
      this.populateForm()
    },

    methods: {
      onSubmitValidate() {
        if (this.totalScore <= 0) {
          this.mNotification({
            error: true,
            title: 'Zero Points',
            message: 'Please fill up at least one test case.',
          })
          return false
        }

        for(var idx=0; idx<this.testCases.length; idx++) {
          const tc = this.testCases[idx]
          if (!tc.varName.isActive && !tc.varType.isActive &&
            !tc.varValue.isActive && !tc.graph.isActive) {
            this.mNotification({
              error: true,
              title: 'Empty Test Case',
              message: `Test Case ${idx+1} should have at least one criteria.`,
            })
            return false     
          }
        }

        if (this.validateAll()) {
          this.question.quiz_id = this.$store.state.quizData.id
          this.question.resource_id = this.$store.state.quizData.resourceId

          // Add tags
          if (this.searchParams.length > 0) {
            this.question.tags = []
            this.searchParams.forEach((item, index) => {
              this.question.tags.push(item)
            })
          }

          this.question.testCases = cloneDeep(this.testCases)
          this.question.score = this.totalScore
          this.question.correct_answer = 0 // Dummy value to pass DB restrictions

          // Create new question
          this.question.isNew = (this.action === 'create') ? true : false
          this.$emit('onProcessing', true)
          this.axios.post('/api/quiz/' + this.question.quiz_id + '/question', this.question)
            .then((result) => {
              this.$emit('onProcessing', false)
              this.mNotification({message: result.data.message})
              this.question.id =  result.data.object.id
              this.$emit('onQuestionSaved')
            })
            .catch((error) => {
              this.mNotification({error: error, message: 'Your Question was not saved.'})
            })            
        } else {
          this.mNotification({
            error: true,
            title: 'Parameters Incomplete',
            message: 'Please check your inputs.',
          })
        }
      },

      onCancelClicked() {
        this.$refs.question.resetFields()
        this.custom_symbols = []
        this.$emit('formCancelClicked')
      },   

      populateForm() {
        if (this.question.tags !== undefined && this.question.tags !== null) {
          this.question.tags.forEach((item, index) => {
            this.searchParams.push(item)
          })
        }

        if (!this.question.testCases) {
          this.addNewTestCase()
        } else {
          this.testCases = cloneDeep(this.question.testCases)
        }
      },

      validateAll() {        
        let isValid = true
        this.$refs['question'].validate(valid => isValid &= valid)

        this.testCases.forEach((test, idx) => {
          if (test.initVar.isActive) {
            isValid &= this.$refs['coding-initVar'][idx].validateAll()
          }

          if (test.varName.isActive) {
            isValid &= this.$refs['coding-varName'][idx].validateAll()
          }

          if (test.varValue.isActive) {
            isValid &= this.$refs['coding-varEquation'][idx].validateAll()
          }

          if (test.varType.isActive) {
            isValid &= this.$refs['coding-varType'][idx].validateAll()
          }

          if (test.graph.isActive) {
            isValid &= this.$refs['coding-graph'][idx].validateAll()
          }
        })
        return isValid
      },

      addNewTestCase() {
        this.testCases.push({})
        this.$set(this.testCases, this.testCases.length-1, cloneDeep(TEST_CASE_DEFAULT))
      },

      removeTestCase(index) {
        this.testCases.splice(index, 1)
      },

      sumScore(list) {
        return list.reduce((s, x) => s + x.score, 0)
      },
    },
  }
</script>

<style scoped>
  .totalScore {
    margin: 20px 15px 0px 15px;
    line-height: 2.5em;
    font-weight: 700;
    color: #5a5e66;
    border-bottom: 1px solid #b1b3b5;
  }

  .gradePanel {
    margin: * 15px * 15px;
    background-color: #fff;
    border: 1px solid #b1b3b5;
    border-radius: 7px;
  }

  .testCasePanel {
    padding: 20px 10px 20px 10px;
    border: 0.5px solid #cccccc;
    margin-top: 5px;
    color: #757575;
  }

  .gradeField {
    padding: 5px 5px 5px 25px;
    border-bottom: 1px solid #e0e0e0;
  }

  .fieldLabel {
    margin-left: 10px;
    font-size: 1em;
  }

  .testLabel {
    font-size: 1em;
    padding-left: 10px;
  }
</style>
